(() => {
  const root = document.documentElement;
  const selector = 'dialog:is(.fz-drawer,.fc-drawer,.fc-sort-drawer,.fn-mobile-dialog,.fn-search-dialog,.fp-lightbox)';
  const managed = new WeakSet();
  const manualLocks = new Set();
  const drawerMotion = new WeakMap();
  const searchMotion = new WeakMap();
  const contentMotion = new Map();
  const finishContent = element => {
    const state = contentMotion.get(element);
    if (!state) return;
    contentMotion.delete(element);
    state.after?.();
    state.animation.cancel();
  };
  window.FiozatoMotion = {
    finish: finishContent,
    run(element, frames, after, duration = 300) {
      finishContent(element);
      if (!element.isConnected || !element.closest('dialog[open]')) {
        after?.();
        return Promise.resolve();
      }
      const animation = element.animate(frames, {duration, easing:'cubic-bezier(.25,.1,.25,1)', fill:'both'});
      const state = {animation, after};
      contentMotion.set(element, state);
      state.done = animation.finished.then(() => {
        if (contentMotion.get(element) === state) finishContent(element);
      }).catch(() => {});
      return state.done;
    },
    reveal(element, visible) {
      const previous = contentMotion.get(element);
      if (previous?.visible === visible) return previous.done;
      if (!previous && element.hidden === !visible) return Promise.resolve();
      const style = getComputedStyle(element);
      const start = {height:element.hidden ? '0px' : element.getBoundingClientRect().height+'px', opacity:element.hidden ? 0 : style.opacity, marginTop:element.hidden ? '0px' : style.marginTop, marginBottom:element.hidden ? '0px' : style.marginBottom, overflow:'hidden'};
      finishContent(element);
      element.hidden = false;
      element.inert = !visible;
      const natural = getComputedStyle(element);
      const end = {height:visible ? element.getBoundingClientRect().height+'px' : '0px', opacity:visible ? 1 : 0, marginTop:visible ? natural.marginTop : '0px', marginBottom:visible ? natural.marginBottom : '0px', overflow:'hidden'};
      const done = this.run(element, [start,end], () => {element.hidden = !visible;element.inert = !visible;}, 280);
      const state = contentMotion.get(element);
      if (state) state.visible = visible;
      return done;
    }
  };
  document.addEventListener('close', event => {
    for (const element of contentMotion.keys()) if (event.target.contains(element)) finishContent(element);
  }, true);
  let position = null;
  let frame = 0;
  let watching = false;
  let pinnedHeader = null;

  const opened = () => document.querySelector(`${selector}[open]`);
  const active = () => Boolean(opened() || manualLocks.size);
  const vp = (k, v) => document.querySelectorAll(selector).forEach(d => d.style.setProperty(k, v));
  const viewport = () => {
    if (!position) return;
    const visual = window.visualViewport;
    if (visual && Math.abs(visual.scale - 1) > 0.01) return;
    const height = visual?.height || window.innerHeight;
    const top = Math.max(0, visual?.offsetTop || 0);
    vp('--fz-viewport-height', `${height}px`);
    vp('--fz-viewport-top', `${top}px`);
    vp('--fz-viewport-bottom', `${Math.max(0, window.innerHeight - height - top)}px`);
  };
  const anchoring = window.CSS?.supports?.('overflow-anchor', 'auto');
  const anchorAt = () => {
    const top = Math.min(window.innerHeight - 1, Math.max(0, document.querySelector('.fn')?.getBoundingClientRect().bottom || 0) + 12);
    let element = document.elementsFromPoint(window.innerWidth / 2, top).find(node => {
      if (node === root || node === document.body || node.closest('.fn,dialog')) return false;
      for (let item = node; item && item !== document.body; item = item.parentElement) if (/sticky|fixed/.test(getComputedStyle(item).position)) return false;
      return true;
    });
    while (element && element.offsetHeight > window.innerHeight) {
      const child = [...element.children].find(node => node.offsetHeight && node.getBoundingClientRect().bottom > top && !/sticky|fixed/.test(getComputedStyle(node).position));
      if (!child) break;
      element = child;
    }
    const chain = [];
    for (let node = element; node && node !== document.body && chain.length < 8; node = node.parentElement) chain.push({ node, top: node.getBoundingClientRect().top });
    return chain.length ? chain : null;
  };
  const restoreY = state => {
    if (!state.anchor || state.moved || !state.y || Math.abs(window.innerWidth - state.width) < 2) return state.y;
    const item = state.anchor.find(({ node }) => node.isConnected && node.getClientRects().length);
    return item ? Math.max(0, Math.round(item.node.getBoundingClientRect().top + window.scrollY - item.top)) : state.y;
  };
  const unlock = () => {
    if (!position) return;
    const previous = position;
    const fixed = root.hasAttribute('data-fz-fixed-body');
    position = null;
    root.removeAttribute('data-fz-scroll-locked');
    root.removeAttribute('data-fz-fixed-body');
    if (pinnedHeader) {
      pinnedHeader.removeAttribute('data-fz-header-pinned');
      pinnedHeader.style.removeProperty('--fz-pinned-header-height');
      pinnedHeader.style.removeProperty('--fz-pinned-header-top');
      pinnedHeader = null;
    }
    document.body.style.removeProperty('top');
    document.querySelectorAll(selector).forEach(d => ['--fz-viewport-height', '--fz-viewport-top', '--fz-viewport-bottom'].forEach(property => d.style.removeProperty(property)));
    if (!fixed && !previous.moved) { window.dispatchEvent(new CustomEvent('fiozato:scroll-restored', { detail: { y: window.scrollY } })); return; }
    const y = restoreY(previous);
    window.scrollTo({ left: previous.x, top: y, behavior: 'instant' });
    window.dispatchEvent(new CustomEvent('fiozato:scroll-restored', { detail: { y } }));
  };
  const lock = () => {
    if (!position) {
      const fixBody = matchMedia('(max-width:1099px), (any-pointer:coarse)').matches;
      position = { x: window.scrollX, y: window.scrollY, width: window.innerWidth, anchor: fixBody && anchoring ? anchorAt() : null };
      const header = document.querySelector('.section-header:has(.fn)');
      if (fixBody && header && !header.hasAttribute('data-fn-hidden')) {
        header.getAnimations().forEach(animation => animation.finish());
        const box = header.getBoundingClientRect();
        if (box.top <= 1 && box.bottom > 0) {
          pinnedHeader = header;
          header.style.setProperty('--fz-pinned-header-height', `${box.height}px`);
          header.style.setProperty('--fz-pinned-header-top', `${box.top}px`);
          header.setAttribute('data-fz-header-pinned', '');
        }
      }
      document.body.style.top = `${-position.y}px`;
      if (fixBody) root.setAttribute('data-fz-fixed-body', '');
      root.setAttribute('data-fz-scroll-locked', '');
    }
    viewport();
  };
  const resize = () => {
    if (!active()) return;
    const previous = position;
    const turned = previous && root.hasAttribute('data-fz-fixed-body') && Math.abs(window.innerWidth - previous.width) > 2;
    if (turned) unlock();
    lock();
    if (turned && !previous.moved) position.anchor = previous.anchor;
  };
  const leave = () => {
    if (!position || !root.hasAttribute('data-fz-fixed-body')) return;
    unlock();
    requestAnimationFrame(() => { if (active()) lock(); });
  };
  const watch = active => {
    if (active === watching) return;
    watching = active;
    const method = active ? 'addEventListener' : 'removeEventListener';
    window[method]('resize', resize);
    window[method]('beforeunload', leave);
    window.visualViewport?.[method]('resize', viewport);
    window.visualViewport?.[method]('scroll', viewport);
  };
  const settle = () => {
    frame = 0;
    if (active()) { lock(); return; }
    watch(false);
    unlock();
  };
  window.FiozatoPageScroll = {
    offset: () => position && root.hasAttribute('data-fz-fixed-body') ? position.y : window.scrollY,
    limit(y) {
      if (!position) return false;
      if (y < position.y) { position.y = Math.max(0, y); position.moved = true; }
      return true;
    },
    lock(token) {
      if (!token) return;
      manualLocks.add(token);
      cancelAnimationFrame(frame);
      lock();
      watch(true);
    },
    unlock(token) {
      if (!token) return;
      manualLocks.delete(token);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(settle);
    }
  };
  const lateral = dialog => dialog.matches('.fz-cart-drawer,.fc-drawer:not(.fc-sort-drawer),.fn-mobile-dialog') || (dialog.matches('.fz-quick-drawer,.fz-notify') && matchMedia('(min-width:750px)').matches);
  const bottom = dialog => dialog.matches('.fc-sort-drawer,.fz-quick-drawer,.fz-notify') && matchMedia('(max-width:749px)').matches;
  const dropdownSort = dialog => dialog.matches('.fc-sort-drawer') && matchMedia('(min-width:750px)').matches;
  const animated = dialog => lateral(dialog) || bottom(dialog) || dropdownSort(dialog);
  const search = dialog => dialog.matches('.fn-search-dialog');
  const stopMotion = dialog => {
    const state = drawerMotion.get(dialog);
    if (!state) return;
    drawerMotion.delete(dialog);
    state.animation.cancel();
  };
  const move = (dialog, closing, quiet) => {
    const previous = drawerMotion.get(dialog);
    if (previous?.closing === closing) return previous.done;
    const vertical = bottom(dialog);
    const dropdown = dropdownSort(dialog);
    const offset = dropdown ? 'translateY(-8px)' : vertical ? 'translateY(100%)' : dialog.matches('.fn-mobile-dialog') ? 'translateX(-100%)' : 'translateX(100%)';
    const resting = vertical || dropdown ? 'translateY(0)' : 'translateX(0)';
    const start = previous || dialog.style.transform ? getComputedStyle(dialog).transform : closing ? resting : offset;
    const startOpacity = dropdown ? (previous ? getComputedStyle(dialog).opacity : closing ? 1 : 0) : null;
    stopMotion(dialog);
    dialog.style.removeProperty('transform');
    if (previous) dialog.style.setProperty('--fz-bd-from',getComputedStyle(dialog,'::backdrop').opacity);
    else dialog.style.removeProperty('--fz-bd-from');
    if (!quiet) dialog.toggleAttribute('data-fz-closing', closing);
    const animation = dialog.animate(dropdown ? [{transform:start,opacity:startOpacity},{transform:closing ? offset : resting,opacity:closing ? 0 : 1}] : [{transform:start},{transform:closing ? offset : resting}], {
      duration:dropdown ? 180 : vertical ? 260 : 300,easing:'cubic-bezier(.25,.1,.25,1)',fill:'both'
    });
    const state = {animation,closing};
    drawerMotion.set(dialog,state);
    state.done = animation.finished.then(() => {
      if (drawerMotion.get(dialog) !== state) return;
      drawerMotion.delete(dialog);
      if (closing) { dialog.close(); settle(); }
      animation.cancel();
    }).catch(() => {});
    return state.done;
  };
  let sheetDrag = null;
  const release = (state, cancelled) => {
    if (!state.dialog.style.transform) return;
    const distance = Math.max(0, state.lastY - state.startY);
    const velocity = distance / Math.max(1, performance.now() - state.startedAt);
    if (cancelled) { move(state.dialog,false); return; }
    if (distance > Math.min(90,state.dialog.getBoundingClientRect().height*.22) || velocity > .45) window.FiozatoDialogs.close(state.dialog);
    else move(state.dialog,false);
  };
  const finishSheetDrag = (event, cancelled = false) => {
    const state = sheetDrag;
    if (!state || event.pointerId !== state.pointerId) return;
    sheetDrag = null;
    try { state.handle.releasePointerCapture?.(state.pointerId); } catch {}
    release(state, cancelled);
  };
  const grab = dialog => {
    const motion = drawerMotion.get(dialog);
    const base = motion ? new DOMMatrix(getComputedStyle(dialog).transform).m42 : 0;
    if (motion) dialog.style.transform = `translate3d(0,${base}px,0)`;
    stopMotion(dialog);
    return base;
  };
  document.addEventListener('pointerdown', event => {
    if (event.target.closest?.('[data-fc-close]')) return;
    const handle = event.target.closest?.('.fc-sort-handle,.fc-sort-drawer .fc-drawer-heading'),dialog = handle?.closest('.fc-sort-drawer[open]');
    if (!dialog || !bottom(dialog) || event.button > 0) return;
    const motion = drawerMotion.get(dialog);
    if (motion?.closing) return;
    sheetDrag = {dialog,handle,pointerId:event.pointerId,startY:event.clientY,lastY:event.clientY,base:grab(dialog),startedAt:performance.now()};
    handle.setPointerCapture?.(event.pointerId);
  }, {passive:true});
  document.addEventListener('pointermove', event => {
    if (!sheetDrag || event.pointerId !== sheetDrag.pointerId) return;
    sheetDrag.lastY = event.clientY;
    sheetDrag.dialog.style.transform = `translate3d(0,${Math.max(0,sheetDrag.base+event.clientY-sheetDrag.startY)}px,0)`;
    event.preventDefault();
  }, {passive:false});
  document.addEventListener('pointerup', event => finishSheetDrag(event));
  document.addEventListener('pointercancel', event => finishSheetDrag(event,true));
  let pull = null;
  const endPull = event => {
    const state = pull;
    if (!state || event.touches.length) return;
    pull = null;
    if (state.base !== null) release(state, event.type === 'touchcancel');
  };
  const pullable = dialog => {
    dialog.addEventListener('touchstart', event => {
      pull = null;
      if (!dialog.open || !bottom(dialog) || event.touches.length > 1 || drawerMotion.get(dialog)?.closing || event.target.closest('button,a') || !event.target.closest('.fz-quick-grip,.fz-quick-media,.fz-notify-product,.fz-notify .fz-dialog-head')) return;
      const y = event.touches[0].clientY;
      pull = {dialog,startX:event.touches[0].clientX,startY:y,lastY:y,startedAt:performance.now(),base:null,photos:event.target.closest('.fz-quick-grip')?null:dialog.querySelector('.fz-quick-photos')};
    }, {passive:true});
    dialog.addEventListener('touchmove', event => {
      if (!pull) return;
      if (event.touches.length > 1) { if (pull.base !== null) release(pull,true); pull = null; return; }
      const y = event.touches[0].clientY;
      if (pull.base === null) {
        if (y === pull.startY) return;
        if (y < pull.startY || Math.abs(event.touches[0].clientX - pull.startX) > y - pull.startY || !event.cancelable || pull.photos?.scrollTop > 0 || pull.dialog.scrollTop > 0) { pull = null; return; }
        pull.base = grab(pull.dialog);
      }
      event.preventDefault();
      pull.lastY = y;
      pull.dialog.style.transform = `translate3d(0,${Math.max(0,pull.base+y-pull.startY)}px,0)`;
    }, {passive:false});
    dialog.addEventListener('touchend', endPull);
    dialog.addEventListener('touchcancel', endPull);
  };
  const stopSearchMotion = dialog => {
    const state = searchMotion.get(dialog);
    if (!state) return;
    searchMotion.delete(dialog);
    state.animation.cancel();
  };
  const fadeSearch = (dialog, closing) => {
    const previous = searchMotion.get(dialog);
    if (previous?.closing === closing) return previous.done;
    const start = previous ? getComputedStyle(dialog).opacity : closing ? 1 : 0;
    if (previous) dialog.style.setProperty('--fz-search-backdrop-from',getComputedStyle(dialog,'::backdrop').opacity);
    stopSearchMotion(dialog);
    dialog.toggleAttribute('data-fz-closing', closing);
    const animation = dialog.animate([{opacity:start},{opacity:closing ? 0 : 1}], {
      duration:closing ? 160 : 200,
      easing:closing ? 'ease-in' : 'ease-out',
      fill:'both'
    });
    const state = {animation,closing};
    searchMotion.set(dialog,state);
    state.done = animation.finished.then(() => {
      if (searchMotion.get(dialog) !== state) return;
      searchMotion.delete(dialog);
      if (closing) { dialog.close(); settle(); }
      animation.cancel();
      dialog.style.removeProperty('--fz-search-backdrop-from');
      dialog.removeAttribute('data-fz-closing');
    }).catch(() => {});
    return state.done;
  };
  document.addEventListener('keydown', event => {
    const dialog = event.target.closest?.('dialog[open]');
    if (event.key === 'Escape' && dialog && (drawerMotion.get(dialog)?.closing || searchMotion.get(dialog)?.closing)) event.preventDefault();
  });
  document.addEventListener('click', event => {
    const dialog = event.target.closest?.('dialog[open]'),motion = drawerMotion.get(dialog);
    if (!dialog || !(motion?.closing || (motion && event.target === dialog) || searchMotion.get(dialog)?.closing)) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);
  const lift = dialog => {
    const under = dialog.fzUnder;
    dialog.removeAttribute('data-fz-over');
    if (!under) return;
    dialog.fzUnder = null;
    stopMotion(under);
    under.close();
  };
  window.FiozatoDialogs = {
    close(dialog) {
      if (!dialog?.open) return Promise.resolve();
      if (!dialog.isConnected) {
        stopMotion(dialog);
        stopSearchMotion(dialog);
        dialog.close();
        settle();
        return Promise.resolve();
      }
      if (search(dialog)) return fadeSearch(dialog,true);
      if (!animated(dialog)) { dialog.close(); settle(); return Promise.resolve(); }
      const under = dialog.fzUnder;
      if (!under) return move(dialog,true);
      dialog.fzUnder = null;
      move(under,true);
      return move(dialog,true,true);
    },
    open(dialog, options = {}) {
      if (!dialog) return;
      if (dialog.open) {
        if (drawerMotion.get(dialog)?.closing || searchMotion.get(dialog)?.closing) {
          if (!dialog.contains(document.activeElement)) dialog.fzFrom = document.activeElement;
          lock();
        }
        if (drawerMotion.get(dialog)?.closing) move(dialog,false);
        if (searchMotion.get(dialog)?.closing) fadeSearch(dialog,false);
        return;
      }
      cancelAnimationFrame(frame);
      if (!managed.has(dialog)) {
        dialog.addEventListener('cancel', event => {
          if (!animated(dialog) && !search(dialog)) return;
          event.preventDefault();
          window.FiozatoDialogs.close(dialog);
        });
        dialog.addEventListener('close', () => {
          if (dialog.open) return;
          stopMotion(dialog);
          stopSearchMotion(dialog);
          dialog.getAnimations().forEach(animation => animation.cancel());
          if (sheetDrag?.dialog === dialog) sheetDrag = null;
          if (pull?.dialog === dialog) pull = null;
          dialog.style.removeProperty('transform');
          dialog.removeAttribute('data-fz-closing');
          dialog.removeAttribute('data-fz-swap');
          lift(dialog);
          if (!document.activeElement?.closest('dialog[open]')) dialog.fzFrom?.focus?.({preventScroll:true});
          dialog.fzFrom = null;
          dialog.style.removeProperty('opacity');
          dialog.style.removeProperty('--fz-search-backdrop-from');
          dialog.style.removeProperty('--fz-bd-from');
          cancelAnimationFrame(frame);
          queueMicrotask(settle);
        });
        if (dialog.matches('[data-quick-dialog],[data-notify-dialog]')) pullable(dialog);
        managed.add(dialog);
      }
      lock();
      let from = 0;
      document.querySelectorAll('fiozato-navigation').forEach(navigation => navigation.showPanel(null));
      if (search(dialog) || animated(dialog)) queueMicrotask(() => document.querySelector('.fn-shade:not([hidden])')?.getAnimations().forEach(animation => { const f = +animation.effect.getKeyframes()[0].opacity; animation.effect.setKeyframes([1,.89,.72,.47,0].map(v => ({opacity:v * f}))); animation.effect.updateTiming(search(dialog) ? {duration:200,easing:'ease-out'} : {duration:300,easing:'ease'}); }));
      try {
        let swap = false, under = null, out = null;
        const stack = [...document.querySelectorAll('dialog[open]')];
        stack.forEach(other => {
          if (other === dialog || other === options.keep || stack.some(item => item.fzUnder === other)) return;
          const motion = drawerMotion.get(other);
          if (!under && animated(dialog) && animated(other) && !motion?.closing) { under = other; return; }
          if (motion?.closing && animated(dialog)) { out = other; if (!dropdownSort(other)) { from = Math.max(from, getComputedStyle(other,'::backdrop').opacity); other.style.setProperty('--fz-bd-from',0); } return; }
          swap ||= !other.hasAttribute('data-fz-closing'); stopMotion(other); stopSearchMotion(other); other.close();
        });
        dialog.toggleAttribute('data-fz-swap', swap || Boolean(under));
        dialog.toggleAttribute('data-fz-over', Boolean(under));
        dialog.fzFrom = (under || out)?.fzFrom ?? document.activeElement;
        dialog.showModal();
        if (under) {
          const a = dialog.getBoundingClientRect(), b = under.getBoundingClientRect();
          const covered = a.left <= b.left + 1 && a.right >= b.right - 1 && a.top <= b.top + 1 && a.bottom >= b.bottom - 1;
          dialog.fzUnder = under;
          Promise.all([move(dialog,false), covered || under.animate({opacity:[1,0]},{duration:260,easing:'cubic-bezier(.25,.1,.25,1)',fill:'both'}).finished.catch(() => {})]).then(() => { if (dialog.fzUnder === under) lift(dialog); });
        } else if (animated(dialog)) { move(dialog,false); if (from) dialog.style.setProperty('--fz-bd-from',from); }
        if (search(dialog)) fadeSearch(dialog,false);
        watch(true);
        viewport();
      } catch (error) {
        settle();
        throw error;
      }
    }
  };
  window.addEventListener('pagehide', () => {
    document.querySelectorAll(`${selector}[open]`).forEach(dialog => dialog.close());
    manualLocks.clear();
    cancelAnimationFrame(frame);
    settle();
  });
  window.addEventListener('pageshow', settle);
})();
